import { Form, Formik, FormikErrors } from 'formik';

import { ResourceType, WebhookGatewayMessage, WebhookResourceTypes } from '../../../../../types/WebhookMessages';
import { Modal } from '../../../../cdl/Modal/Modal';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { translate } from '../../../../common/helpers/translate.helper';
import { useWebhookUpdate } from '../hooks/useWebhookUpdate';

import { EditWebhookFormContent, EditWebhookFormState } from './EditWebhookFormContent';

interface EditWebhookModalProps {
    webhook: WebhookGatewayMessage;
    groupId: string;
    isOpen: boolean;
    onDismiss: () => void;
}

export const EditWebhookModal = ({ webhook, groupId, isOpen, onDismiss }: EditWebhookModalProps) => {
    const webhookUpdateMutation = useWebhookUpdate();
    const { addToast, addErrorToast } = useToasts();

    const initialValues: EditWebhookFormState = {
        url: webhook.url,
        customerIds: webhook.customerIds,
        resourceTypes: {
            draftEnquiry: webhook.resourceTypes.includes(WebhookResourceTypes.DRAFT_ENQUIRY),
            enquiry: webhook.resourceTypes.includes(WebhookResourceTypes.ENQUIRY),
            offer: webhook.resourceTypes.includes(WebhookResourceTypes.OFFER),
            approvalRequest: webhook.resourceTypes.includes(WebhookResourceTypes.APPROVAL_REQUEST),
        },
    };

    const validate = (values: EditWebhookFormState) => {
        const errors: FormikErrors<EditWebhookFormState> = {};

        const urlRegex = new RegExp(/^https:\/\//);

        if (!values.url) {
            errors.url = translate('webhooks.validation.endpointUrlRequired');
        } else if (values.url && !urlRegex.test(values.url)) {
            errors.url = translate('webhooks.validation.endpointUrlInvalid');
        }

        return errors;
    };

    const handleSubmit = (values: EditWebhookFormState) => {
        const resourceTypes = [
            values.resourceTypes.draftEnquiry ? WebhookResourceTypes.DRAFT_ENQUIRY : undefined,
            values.resourceTypes.enquiry ? WebhookResourceTypes.ENQUIRY : undefined,
            values.resourceTypes.offer ? WebhookResourceTypes.OFFER : undefined,
            values.resourceTypes.approvalRequest ? WebhookResourceTypes.APPROVAL_REQUEST : undefined,
        ].filter(Boolean) as ResourceType[];

        const webhookRequest = {
            name: webhook.name,
            url: values.url,
            customerIds: values.customerIds,
            groupId,
            resourceTypes,
            enabled: webhook.enabled,
        };

        webhookUpdateMutation.mutate(
            { webhookId: webhook.id, webhook: webhookRequest },
            {
                onSuccess: () => {
                    onDismiss();
                    addToast(translate('webhooks.editSuccess'));
                },
                onError: () => {
                    addErrorToast(translate('webhooks.editError'));
                },
            }
        );
    };

    return (
        <Modal isOpen={isOpen} scrollable={false} onDismiss={onDismiss} dismissible={false}>
            <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
                <Form>
                    <EditWebhookFormContent onDismiss={onDismiss} groupId={groupId} />
                </Form>
            </Formik>
        </Modal>
    );
};
